/**
 * I conti che servono solo ai widget della dashboard.
 *
 * Partono dagli stessi dati e dalle stesse regole della pagina Analisi e
 * delle Statistiche Lead (kpiFatturato.ts, kpiLead.ts). Qui ci sono i tagli
 * da dashboard: il periodo in corso contro lo stesso tratto dell'anno prima,
 * gli ultimi mesi, le liste "da fare" in ordine di urgenza.
 */

import { getSubscriptionStatus, type SubscriptionStatus } from '../subscriptionUtils';
import {
  leadNextActionDue,
  leadStaleness,
  type DueLeadReminder,
  type Lead,
} from '../../types/leads.types';
import { MESI_BREVI, TIPI_FISCALI, type AggregatiAnalisi, type FatturaAnalisi, type ScadenzaAperta } from './kpiFatturato';
import { statisticheLead, type VoceConteggio } from './kpiLead';
import type { AbbonamentoDashboard, CampagnaDashboard, OffertaDashboard } from './fonti';

const GIORNO_MS = 86_400_000;

export interface Confronto {
  valore: number;
  precedente: number;
  /** In percentuale intera; null se prima non c'era niente con cui confrontare. */
  variazione: number | null;
}

export function variazionePercentuale(valore: number, precedente: number): number | null {
  if (!precedente) return null;
  return Math.round(((valore - precedente) / Math.abs(precedente)) * 100);
}

/** Giorni fra due date AAAA-MM-GG (b - a). */
export function giorniFra(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / GIORNO_MS);
}

// ---------------------------------------------------------------------------
// Fatturato
// ---------------------------------------------------------------------------

export type PeriodoFatturato = 'mese' | 'trimestre' | 'anno';

/** Il primo giorno del periodo che contiene `oggi`. */
export function inizioPeriodo(oggi: string, periodo: PeriodoFatturato): string {
  const anno = oggi.slice(0, 4);
  if (periodo === 'anno') return `${anno}-01-01`;
  if (periodo === 'mese') return `${oggi.slice(0, 7)}-01`;
  const primoMese = Math.floor((Number(oggi.slice(5, 7)) - 1) / 3) * 3 + 1;
  return `${anno}-${String(primoMese).padStart(2, '0')}-01`;
}

/** La stessa data un anno prima; il 29 febbraio diventa il 28. */
export function annoPrima(data: string): string {
  const anno = Number(data.slice(0, 4)) - 1;
  const meseGiorno = data.slice(5);
  return `${anno}-${meseGiorno === '02-29' ? '02-28' : meseGiorno}`;
}

function imponibileTra(fatture: readonly FatturaAnalisi[], da: string, a: string): number {
  return fatture.reduce((somma, f) => {
    if (!TIPI_FISCALI.has(f.document_type) || !f.invoice_date) return somma;
    const giorno = f.invoice_date.slice(0, 10);
    if (giorno < da || giorno > a) return somma;
    const segno = f.document_type === 'credit_note' ? -1 : 1;
    return somma + (f.taxable_amount || 0) * segno;
  }, 0);
}

/**
 * Imponibile emesso dall'inizio del periodo a oggi, contro lo stesso tratto
 * dell'anno prima (fino allo stesso giorno: il confronto e' alla pari).
 */
export function confrontoFatturato(
  fatture: readonly FatturaAnalisi[],
  periodo: PeriodoFatturato,
  oggi: string,
): Confronto {
  const inizio = inizioPeriodo(oggi, periodo);
  const valore = imponibileTra(fatture, inizio, oggi);
  const precedente = imponibileTra(fatture, annoPrima(inizio), annoPrima(oggi));
  return { valore, precedente, variazione: variazionePercentuale(valore, precedente) };
}

export interface MeseAndamento {
  chiave: string;
  etichetta: string;
  emesso: number;
  incassato: number;
  costi: number;
}

/** Gli ultimi `mesi` mesi fino a quello in corso, a cavallo d'anno se serve. */
export function andamentoMensile(aggregati: AggregatiAnalisi, mesi: number, oggi: string): MeseAndamento[] {
  const annoOggi = Number(oggi.slice(0, 4));
  const meseOggi = Number(oggi.slice(5, 7)) - 1;
  const risultato: MeseAndamento[] = [];
  for (let indietro = mesi - 1; indietro >= 0; indietro--) {
    const assoluto = annoOggi * 12 + meseOggi - indietro;
    const anno = Math.floor(assoluto / 12);
    const mese = assoluto % 12;
    risultato.push({
      chiave: `${anno}-${mese}`,
      etichetta: anno === annoOggi ? MESI_BREVI[mese] : `${MESI_BREVI[mese]} ${String(anno).slice(2)}`,
      emesso: aggregati.emessoPerAnno.get(anno)?.[mese] ?? 0,
      incassato: aggregati.incassatoPerAnno.get(anno)?.[mese] ?? 0,
      costi: aggregati.costiPerAnno.get(anno)?.[mese] ?? 0,
    });
  }
  return risultato;
}

export interface RataScaduta extends ScadenzaAperta {
  giorniRitardo: number;
}

/** Le rate scadute, dalla piu' in ritardo. */
export function rateScadute(scadenze: readonly ScadenzaAperta[], oggi: string): RataScaduta[] {
  return scadenze
    .filter((s): s is ScadenzaAperta & { scadenza: string } => s.scaduta && !!s.scadenza)
    .map((s) => ({ ...s, giorniRitardo: giorniFra(s.scadenza, oggi) }))
    .sort((a, b) => b.giorniRitardo - a.giorniRitardo);
}

// ---------------------------------------------------------------------------
// Lead
// ---------------------------------------------------------------------------

export type MotivoRichiamo = 'promemoria' | 'azione' | 'fermo';

export interface LeadDaRichiamare {
  leadId: string;
  nome: string;
  motivo: MotivoRichiamo;
  dettaglio: string;
  /** Da quanto tempo la cosa e' dovuta, in millisecondi. */
  ritardo: number;
}

const PRIORITA: Record<MotivoRichiamo, number> = { promemoria: 0, azione: 1, fermo: 2 };

export function nomeLead(lead: Pick<Lead, 'full_name' | 'company_name'>): string {
  return lead.full_name || lead.company_name || 'Lead senza nome';
}

/**
 * Chi va richiamato: promemoria scaduti, prossime azioni in ritardo, lead
 * attivi fermi oltre la soglia. Un lead compare una volta sola, con il motivo
 * piu' urgente; vinti e persi non compaiono.
 */
export function leadDaRichiamare(
  leads: readonly Lead[],
  promemoria: readonly DueLeadReminder[],
  giorniRicontatto: number,
  adesso: number,
  soloDi: string | null = null,
): LeadDaRichiamare[] {
  const aperti = new Map(
    leads
      .filter((l) => l.stage !== 'vinto' && l.stage !== 'perso' && (!soloDi || l.assigned_to === soloDi))
      .map((l) => [l.id, l]),
  );
  const perLead = new Map<string, LeadDaRichiamare>();
  const proponi = (voce: LeadDaRichiamare) => {
    const attuale = perLead.get(voce.leadId);
    const piuUrgente = !attuale
      || PRIORITA[voce.motivo] < PRIORITA[attuale.motivo]
      || (voce.motivo === attuale.motivo && voce.ritardo > attuale.ritardo);
    if (piuUrgente) perLead.set(voce.leadId, voce);
  };

  for (const p of promemoria) {
    const lead = p.event?.lead_id ? aperti.get(p.event.lead_id) : undefined;
    if (!lead) continue;
    proponi({ leadId: lead.id, nome: nomeLead(lead), motivo: 'promemoria', dettaglio: p.event?.title || 'Promemoria', ritardo: adesso - Date.parse(p.remind_at) });
  }
  for (const lead of aperti.values()) {
    if (leadNextActionDue(lead, 0, adesso) && lead.next_action_at) {
      proponi({ leadId: lead.id, nome: nomeLead(lead), motivo: 'azione', dettaglio: lead.next_action || 'Azione in ritardo', ritardo: adesso - Date.parse(lead.next_action_at) });
    }
    const fermo = leadStaleness(lead, giorniRicontatto, adesso);
    if (fermo.level !== 'fresh') {
      proponi({ leadId: lead.id, nome: nomeLead(lead), motivo: 'fermo', dettaglio: `Fermo da ${fermo.days} giorni`, ritardo: fermo.days * GIORNO_MS });
    }
  }

  return [...perLead.values()].sort((a, b) => PRIORITA[a.motivo] - PRIORITA[b.motivo] || b.ritardo - a.ritardo);
}

export type PeriodoLead = 'settimana' | 'mese';

/** Lead arrivati negli ultimi 7 o 30 giorni, contro i 7 o 30 prima. */
export function nuoviLead(leads: readonly Lead[], periodo: PeriodoLead, adesso: number): Confronto {
  const durata = (periodo === 'settimana' ? 7 : 30) * GIORNO_MS;
  const tra = (da: number, a: number) => leads.filter((l) => {
    const t = Date.parse(l.created_at);
    return t > da && t <= a;
  }).length;
  const valore = tra(adesso - durata, adesso);
  const precedente = tra(adesso - 2 * durata, adesso - durata);
  return { valore, precedente, variazione: variazionePercentuale(valore, precedente) };
}

export type PeriodoFonte = 'mese' | 'anno' | 'sempre';

/** Da dove arrivano i lead, negli ultimi 30 o 365 giorni o da sempre. */
export function leadPerFonte(leads: readonly Lead[], periodo: PeriodoFonte, adesso: number): VoceConteggio[] {
  const giorni = periodo === 'mese' ? 30 : periodo === 'anno' ? 365 : null;
  const scelti = giorni === null ? leads : leads.filter((l) => Date.parse(l.created_at) > adesso - giorni * GIORNO_MS);
  return statisticheLead(scelti, 1, new Date(adesso)).bySource;
}

// ---------------------------------------------------------------------------
// Offerte, campagne, abbonamenti
// ---------------------------------------------------------------------------

export interface StatoOfferte {
  /** Inviate e ancora valide: aspettano una risposta. */
  inAttesa: OffertaDashboard[];
  /** Inviate ma oltre la validita', senza risposta. */
  scadute: number;
  /** Accettate, in tutto o in parte da fatturare. */
  daFatturare: number;
}

export function statoOfferte(offerte: readonly OffertaDashboard[], oggi: string, soloDi: string | null = null): StatoOfferte {
  const mie = soloDi ? offerte.filter((o) => o.created_by === soloDi) : offerte;
  const inviate = mie.filter((o) => o.status === 'sent');
  const valida = (o: OffertaDashboard) => !o.valid_until || o.valid_until.slice(0, 10) >= oggi;
  return {
    inAttesa: inviate.filter(valida),
    scadute: inviate.filter((o) => !valida(o)).length,
    daFatturare: mie.filter((o) => o.status === 'accepted' || o.status === 'partially_invoiced').length,
  };
}

export interface EsitoCampagna {
  inviate: number;
  /** Sulle consegnate (o sulle inviate, se le consegne non sono ancora note). */
  aperturePct: number | null;
  clicPct: number | null;
}

export function esitoCampagna(c: CampagnaDashboard): EsitoCampagna {
  const base = c.delivered_count || c.sent_count;
  const pct = (n: number) => (base ? Math.round((n / base) * 100) : null);
  return { inviate: c.sent_count, aperturePct: pct(c.open_count), clicPct: pct(c.click_count) };
}

export interface AbbonamentoInScadenza extends AbbonamentoDashboard {
  statoRinnovo: SubscriptionStatus;
  giorni: number;
}

/** Abbonamenti attivi scaduti o in scadenza entro 30 giorni, dal piu' vicino. */
export function abbonamentiInScadenza(abbonamenti: readonly AbbonamentoDashboard[], oggi: string): AbbonamentoInScadenza[] {
  return abbonamenti
    .filter((a): a is AbbonamentoDashboard & { next_renewal_date: string } => a.status === 'active' && !!a.next_renewal_date)
    .map((a) => ({
      ...a,
      statoRinnovo: getSubscriptionStatus(a.next_renewal_date, oggi),
      giorni: giorniFra(oggi, a.next_renewal_date.slice(0, 10)),
    }))
    .filter((a) => a.statoRinnovo !== 'ok')
    .sort((a, b) => a.giorni - b.giorni);
}
